import { create } from "zustand";
import { auth } from '../../../../auth';
import convex, { waitForConvexAuthReady } from "../../../../convexClient";
import { api } from "../../../../convex/_generated/api";
import { withActiveTenant } from "../../../../utils/tenantUtils";

const normalizeMealPlan = (doc) => ({
  id: doc._id || doc.id,
  weekStartDate: doc?.weekStartDate ?? "",
  meals: doc?.meals ?? {},
  generatedByAI: doc?.generatedByAI ?? false,
  aiPrompt: doc?.aiPrompt ?? "",
  userId: doc?.userId,
  metadata: {
    userId: doc?.metadata?.userId,
    createdAt: doc?.metadata?.createdAt ?? null,
    updatedAt: doc?.metadata?.updatedAt ?? null,
  },
  sharedWith: Array.isArray(doc?.sharedWith) ? doc.sharedWith : [],
});

const normalizeRecipe = (doc) => ({
  id: doc._id || doc.id,
  mealName: doc?.mealName ?? "",
  imageUrl: doc?.imageUrl ?? "",
  videoUrl: doc?.videoUrl ?? "",
  cookingTime: doc?.cookingTime ?? "",
  difficulty: doc?.difficulty ?? "Medium",
  servings: doc?.servings ?? "",
  ingredients: Array.isArray(doc?.ingredients) ? doc.ingredients : [],
  steps: Array.isArray(doc?.steps) ? doc.steps : [],
  category: doc?.category ?? "",
  cuisine: doc?.cuisine ?? "Ghanaian",
  isFavorite: doc?.isFavorite ?? false,
  userId: doc?.userId,
  metadata: {
    userId: doc?.metadata?.userId,
    createdAt: doc?.metadata?.createdAt ?? null,
    updatedAt: doc?.metadata?.updatedAt ?? null,
  },
});

const useMealPlanStore = create((set) => ({
  mealPlans: [],
  recipes: [],
  currentWeekPlan: null,
  loading: false,
  error: null,

  fetchMealPlans: async () => {
    const user = auth.currentUser;
    if (!user) { set({ mealPlans: [], error: "Not authenticated" }); return; }
    set({ loading: true, error: null });
    try {
      await waitForConvexAuthReady();
      const docs = await convex.query(api.mealPlans.listMealPlans, withActiveTenant());
      set({ mealPlans: docs.map(normalizeMealPlan), loading: false });
    } catch (e) {
      set({ loading: false, error: e.message || "Failed to fetch meal plans" });
    }
  },

  fetchRecipes: async () => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      await waitForConvexAuthReady();
      const docs = await convex.query(api.mealPlans.listRecipes, withActiveTenant());
      set({ recipes: docs.map(normalizeRecipe) });
    } catch (e) {
      console.error("Error fetching recipes:", e);
    }
  },

  createMealPlan: async (payload) => {
    const user = auth.currentUser;
    if (!user) throw new Error("Not authenticated");

    const id = await convex.mutation(api.mealPlans.createMealPlan, withActiveTenant({
      weekStartDate: payload.weekStartDate,
      meals: payload.meals || {},
      generatedByAI: payload.generatedByAI || false,
      aiPrompt: payload.aiPrompt || "",
      sharedWith: Array.isArray(payload.sharedWith) ? payload.sharedWith : [],
    }));

    const newPlan = normalizeMealPlan({
      _id: id, ...payload, userId: user.uid,
      metadata: { userId: user.uid, createdAt: Date.now(), updatedAt: Date.now() },
    });

    set((s) => ({ mealPlans: [newPlan, ...s.mealPlans] }));
    return id;
  },

  updateMealPlan: async (id, updates) => {
    const user = auth.currentUser;
    if (!user) throw new Error("Not authenticated");
    await convex.mutation(api.mealPlans.updateMealPlan, withActiveTenant({ id, ...updates }));
    set((s) => ({
      mealPlans: s.mealPlans.map((p) =>
        p.id === id
          ? { ...p, ...updates, metadata: { ...p.metadata, updatedAt: Date.now() } }
          : p
      ),
    }));
  },

  deleteMealPlan: async (id) => {
    await convex.mutation(api.mealPlans.deleteMealPlan, { id });
    set((s) => ({ mealPlans: s.mealPlans.filter((p) => p.id !== id) }));
  },

  saveRecipe: async (payload) => {
    const user = auth.currentUser;
    if (!user) throw new Error("Not authenticated");

    const id = await convex.mutation(api.mealPlans.createRecipe, withActiveTenant({
      mealName: payload.mealName?.trim() ?? "",
      imageUrl: payload.imageUrl ?? "",
      videoUrl: payload.videoUrl ?? "",
      cookingTime: payload.cookingTime ?? "",
      difficulty: payload.difficulty ?? "Medium",
      servings: payload.servings ?? "",
      ingredients: payload.ingredients || [],
      steps: payload.steps || [],
      category: payload.category ?? "",
      cuisine: payload.cuisine ?? "Ghanaian",
      isFavorite: payload.isFavorite ?? false,
    }));

    const newRecipe = normalizeRecipe({
      _id: id, ...payload, userId: user.uid,
      metadata: { userId: user.uid, createdAt: Date.now(), updatedAt: Date.now() },
    });

    set((s) => ({ recipes: [newRecipe, ...s.recipes] }));
    return id;
  },

  updateRecipe: async (id, updates) => {
    const user = auth.currentUser;
    if (!user) throw new Error("Not authenticated");
    await convex.mutation(api.mealPlans.updateRecipe, withActiveTenant({ id, ...updates }));
    set((s) => ({
      recipes: s.recipes.map((r) =>
        r.id === id
          ? { ...r, ...updates, metadata: { ...r.metadata, updatedAt: Date.now() } }
          : r
      ),
    }));
  },

  deleteRecipe: async (id) => {
    await convex.mutation(api.mealPlans.deleteRecipe, { id });
    set((s) => ({ recipes: s.recipes.filter((r) => r.id !== id) }));
  },

  setCurrentWeekPlan: (plan) => set({ currentWeekPlan: plan }),
}));

export default useMealPlanStore;
